import {
  StatusIcon,
  AssigneeAvatar,
  NameLabel,
  ProgressRateLabel,
} from './ganttSliderStyles'
import { LabelMode, WbsItemForDisplay } from './useLabelMode'
import { GanttDisplayOptions } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import {
  getWbsItemStatusIconColor,
  getWbsItemStatusLabel,
} from '../../../../../../../../../lib/functions/wbsItem'

type GanttLabelContentProps = {
  isParentType: boolean
  displayOptions?: GanttDisplayOptions
  wbsItemForDisplay?: WbsItemForDisplay
  progressRate: number
  labelMode: LabelMode
  hasCumulation: boolean
}

/** ラベル表示対象が1つでもあるかを判定する */
export const hasAnyContent = ({
  isParentType,
  displayOptions,
  wbsItemForDisplay,
  hasCumulation,
}: Pick<
  GanttLabelContentProps,
  'isParentType' | 'displayOptions' | 'wbsItemForDisplay' | 'hasCumulation'
>): boolean => {
  const hasStatus =
    !isParentType && !!displayOptions?.showStatus && !!wbsItemForDisplay?.status
  const hasName = !!displayOptions?.showName && !!wbsItemForDisplay?.displayName
  const hasAssignee =
    !isParentType &&
    !!displayOptions?.showAssignee &&
    !!wbsItemForDisplay?.responsible
  const hasProgressRate = !!displayOptions?.showProgressRate && hasCumulation
  return hasStatus || hasName || hasAssignee || hasProgressRate
}

/** ホバー用ツールチップテキストを組み立てる */
export const buildHoverText = ({
  wbsItemForDisplay,
  progressRate,
  hasCumulation,
}: Pick<
  GanttLabelContentProps,
  'wbsItemForDisplay' | 'progressRate' | 'hasCumulation'
>): string =>
  [
    wbsItemForDisplay?.status
      ? getWbsItemStatusLabel(wbsItemForDisplay.status)
      : undefined,
    wbsItemForDisplay?.displayName,
    wbsItemForDisplay?.responsible?.name,
    hasCumulation ? `${(progressRate * 100).toFixed(1)}%` : undefined,
  ]
    .filter(Boolean)
    .join(' / ')

/** ラベル内のコンテンツ（ステータスアイコン・名前・担当者・進捗率） */
export const GanttLabelContent = ({
  isParentType,
  displayOptions,
  wbsItemForDisplay,
  progressRate,
  labelMode,
  hasCumulation,
}: GanttLabelContentProps) => {
  const hasStatus =
    !isParentType && !!displayOptions?.showStatus && !!wbsItemForDisplay?.status
  const hasName = !!displayOptions?.showName && !!wbsItemForDisplay?.displayName
  const hasAssignee =
    !isParentType &&
    !!displayOptions?.showAssignee &&
    !!wbsItemForDisplay?.responsible
  const hasProgressRate = !!displayOptions?.showProgressRate && hasCumulation

  return (
    <>
      {hasStatus && wbsItemForDisplay?.status && (
        <StatusIcon
          $color={getWbsItemStatusIconColor(wbsItemForDisplay.status)}
        />
      )}
      {hasName && wbsItemForDisplay?.displayName && (
        <NameLabel $inside={labelMode === 'inside'} $isParent={isParentType}>
          {wbsItemForDisplay.displayName}
        </NameLabel>
      )}
      {hasAssignee && wbsItemForDisplay?.responsible && (
        <AssigneeAvatar
          src={wbsItemForDisplay.responsible.iconUrl}
          alt={wbsItemForDisplay.responsible.name}
        >
          {wbsItemForDisplay.responsible.name?.charAt(0)}
        </AssigneeAvatar>
      )}
      {hasProgressRate && (
        <ProgressRateLabel
          $inside={labelMode === 'inside'}
          $isParent={isParentType}
          $showSeparator={hasStatus || hasName || hasAssignee}
        >
          {(progressRate * 100).toFixed(1)}%
        </ProgressRateLabel>
      )}
    </>
  )
}
